import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'

import {
  StyledCashbackContent,
  StyledProgressContent,
  StyledProgress,
  CashbackCardContainer,
  CardWrapper,
  EmptyContentWrapper,
  StyledNoticeText,
  ButtonContainer,
  TextContainer,
  StyledTimerContainer,
} from './styles'

import { useTranslation } from '../../../context/translationProvider'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { 
  INGAME_CTA_ACTIONS,
  INGAME_DRAWERS,
  INGAME_TABS,
} from '../../../utils/constants'
import { Title, Text } from '../../atoms/text'
import { FormatAmount } from '../../atoms/formatAmount'
import { Timer } from '../../atoms/timer'
import { CashbackCard } from '../../molecules/cashbackCard'
import { Tabs, Tab } from '../../atoms/tabs'
import { Button } from '../../atoms/button'
import { Image } from '../../atoms/image'
import { If } from '../../atoms/if'
import Link from '../../atoms/link'

import CashinCashbackIcon from '../../../images/cashback/icon_cashback.svg'
import { themeStyles } from '../../../styles/theme'

export const InGameCashbackSection = ({
  vaultAmount,
  activeCards,
  claimedCards,
  shorterDeadline,
  user,
  loadingCashout,
  cashout,
  getClaimedData,
  hasMoreClaimedCards,
  isDrawerOpen,
}) => {
  const { translate } = useTranslation()
  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS, getTrackingContext } = useEventTracking()
  const [tab, setTab] = useState(0)
  const [maxHeight, setMaxHeight] = useState(0)
  const activeTabRef = useRef(null)

  const currency = user?.wallet?.currency

  const isFirstTab = tab === 0
  const isVaultAmountZero = vaultAmount === 0
  const isButtonDisabled = isVaultAmountZero || loadingCashout

  const getData = async () => {
    await getClaimedData({ reset: true })
  }

  useEffect(() => {
    const currentTab = isFirstTab ? INGAME_TABS.ACTIVE : INGAME_TABS.CLAIMED
    if (!isDrawerOpen) { return }
    const cashback_available = activeCards && activeCards.length > 0

    trackEvent(EVENT_NAMES.INGAME_DRAWER_OPENED, {
      drawer: INGAME_DRAWERS.CASHBACK,
      tab: currentTab,
      cashback_available,
      cashback_amount: vaultAmount,
      ...getTrackingContext(),
    }, true)
    // important: this should only be called once when drawer is opened.
  }, [isDrawerOpen, isFirstTab])

  useEffect(() => {
    if (!isFirstTab) {
      getData()
    }
  }, [tab])

  useEffect(() => {
    if (activeTabRef.current) {
      setMaxHeight(activeTabRef.current.offsetHeight)
    }
  }, [vaultAmount, activeCards])

  const getVaultProgressValue = (vaultAmount) =>
    ((vaultAmount % 1) * 100).toFixed(0)

  const getShorterDeadlineDate = (shorterDeadline) => {
    return shorterDeadline?.date
      ? new Date(shorterDeadline.date)
      : new Date('1970-01-01')
  }

  const handleCashout = () => {
    trackEvent(EVENT_NAMES.INGAME_ACTION_TAKEN, {
      drawer: INGAME_DRAWERS.CASHBACK,
      cta: INGAME_CTA_ACTIONS.CLAIM_CASHBACK,
      amount: vaultAmount,
      site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
    }, true)
    
    cashout()
  }

  const renderCashoutButton = () => (
    <ButtonContainer>
      <Button expand disabled={isButtonDisabled} onClick={handleCashout}>
        {translate('vault.cashout')}{' '}
        <FormatAmount
          amount={vaultAmount}
          shortCode={currency?.short_code}
          currencySymbol={currency?.symbol}
          minimumFractionDigits={2}
          maximumFractionDigits={2}
        />
      </Button>
    </ButtonContainer>
  )

  const renderEmptyState = (isActiveTab = false) => (
    <EmptyContentWrapper>
      <Image url={CashinCashbackIcon} width={150} />
      <If
        condition={!isActiveTab}
         render={() => (
          <Text>{translate('game.cashback.claimed.empty.paragraph')}</Text>
        )}
        renderElse={() => (
          <>
            <Text>{translate('game.cashback.active.empty.sub-heading')}</Text>
            <StyledNoticeText>
              {translate('game.cashback.active.empty.paragraph')}
            </StyledNoticeText>
            <Link to="/promo/cassino-cashback/">
              {translate('game.cashback.active.empty.text-link')}
            </Link>
            {renderCashoutButton()}
          </>
        )}
      />
    </EmptyContentWrapper>
  )

  const renderActiveTab = () => {
    if (!vaultAmount) return renderEmptyState(true)

    return (
      <>
        <StyledProgressContent>
          <StyledProgress>
            <CircularProgressbar
              minValue={0}
              maxValue={100}
              strokeWidth={10}
              value={getVaultProgressValue(vaultAmount)}
              styles={buildStyles({
                pathTransitionDuration: 1,
                pathColor: themeStyles.colors.accentColors.secondary.main,
                trailColor: themeStyles.colors.dominantGrey.main,
                strokeLinecap: 'butt',
              })}
            />
          </StyledProgress>
          <div>
            <Title
              level={1}
              style={{
                color: themeStyles.colors.accentColors.secondary.main,
                fontStyle: 'normal',
                fontFamily: 'Inter',
                fontSize: 25,
              }}
            >
              <FormatAmount
                amount={vaultAmount}
                shortCode={currency?.short_code}
                currencySymbol={currency?.symbol}
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </Title>
            <Text style={{ fontSize: 16 }}>
              {translate('vault.activeForMessage')}
            </Text>
            <StyledTimerContainer>
              <Timer
                endDate={getShorterDeadlineDate(shorterDeadline)}
                onFinish={() => {}}
                expiryReminder={shorterDeadline?.expiryTime}
              />
            </StyledTimerContainer>
          </div>
        </StyledProgressContent>

        <TextContainer>
          <Title level={2}>
            {translate('game.cashback.active.sub-heading')}
          </Title>
          <Text style={{ fontSize: 14 }}>
            {translate('game.cashback.active.paragraph')}
          </Text>
        </TextContainer>

        <CashbackCardContainer>
          {activeCards.map((item) => (
            <CashbackCard
              key={item.id}
              type="large"
              value={item.amount}
              endDate={item.expiryDate}
              currency={currency?.short_code}
              currencySymbol={currency?.symbol}
              cashbackType={item.type}
            />
          ))}
        </CashbackCardContainer>

        {renderCashoutButton()}
      </>
    )
  }

  const renderClaimedTab = () => {
    if (!claimedCards.length) return renderEmptyState(false)

    return (
      <>
        <CardWrapper>
          {claimedCards.map((item) => (
            <CashbackCard
              key={item.id}
              type="small"
              value={item.claimedAmount}
              claimDate={item.claimDate}
              currency={currency?.short_code}
              currencySymbol={currency?.symbol}
              cashbackType={item.type}
            />
          ))}
        </CardWrapper>

        <If
          condition={hasMoreClaimedCards}
          render={() => (
            <Button
              onClick={() => getClaimedData({ reset: false })}
              bordered
              expand
            >
              {translate('common.loadMore')}
            </Button>
          )}
        />
      </>
    )
  }

  return (
    <>
      <Tabs expandChildren underlineChildren hideGap fullWidthButton>
        <Tab
          onSelect={setTab}
          label={translate('vault.active')}
          value={0}
          active={tab}
        />
        <Tab
          onSelect={setTab}
          label={translate('vault.claimed')}
          value={1}
          active={tab}
        />
      </Tabs>

      <StyledCashbackContent $minHeight={maxHeight}>
        <If
          condition={isFirstTab}
          render={() => <div ref={activeTabRef}>{renderActiveTab()}</div>}
          renderElse={() => <div>{renderClaimedTab()}</div>}
        />
      </StyledCashbackContent>
    </>
  )
}

InGameCashbackSection.propTypes = {
  vaultAmount: PropTypes.number,
  activeCards: PropTypes.array,
  claimedCards: PropTypes.array,
  shorterDeadline: PropTypes.object,
  user: PropTypes.object,
  loadingCashout: PropTypes.bool,
  cashout: PropTypes.func,
  getClaimedData: PropTypes.func,
  hasMoreClaimedCards: PropTypes.bool,
  isDrawerOpen: PropTypes.bool,
}
